import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Loader2, BarChart3, Briefcase, Coins, TrendingUp, TrendingDown, Percent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { ProfitCell, MarginCell } from "@/components/ProfitCell";
import { api, formatApiError } from "@/lib/api";
import {
  JOB_STATUS_FLOW,
  JOB_STATUS_LABELS,
  jobStatusTone,
  ORDER_STATUS_FLOW,
  ORDER_STATUS_LABELS,
  orderStatusTone,
} from "@/lib/constants";
import { formatCurrency, formatDate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

function getTurkeyDefaultDates() {
  const now = new Date();
  const year = now.getFullYear();
  const startStr = `${year}-01-01`;

  const todayMonth = String(now.getMonth() + 1).padStart(2, "0");
  const todayDay = String(now.getDate()).padStart(2, "0");
  const endStr = `${year}-${todayMonth}-${todayDay}`;

  return { startStr, endStr };
}

export default function ReportsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { settings } = useAuth();
  const minMargin = settings?.min_margin_percent ?? 20;

  const { startStr: defStart, endStr: defEnd } = getTurkeyDefaultDates();

  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "customer");

  // --- CUSTOMER REPORT STATE ---
  const [startDate, setStartDate] = useState(searchParams.get("start_date") || defStart);
  const [endDate, setEndDate] = useState(searchParams.get("end_date") || defEnd);
  const [dateType, setDateType] = useState(searchParams.get("date_type") || "Sözleşme Tarihi");
  const [salespersonId, setSalespersonId] = useState(searchParams.get("salesperson_id") || "");
  const [productCategoryId, setProductCategoryId] = useState(searchParams.get("product_category_id") || "");
  const [status, setStatus] = useState(() => {
    return searchParams.getAll("status").filter(s => s && s !== "__all__");
  });
  const [includeQuotations, setIncludeQuotations] = useState(searchParams.get("include_quotations") === "true");
  const [includeArchived, setIncludeArchived] = useState(searchParams.get("include_archived") === "true");
  const [includeUndated, setIncludeUndated] = useState(searchParams.get("include_undated") === "true");
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get("page_size") || "25", 10));

  // --- ORDER REPORT STATE ---
  const [orderStartDate, setOrderStartDate] = useState(searchParams.get("order_start_date") || defStart);
  const [orderEndDate, setOrderEndDate] = useState(searchParams.get("order_end_date") || defEnd);
  const [orderSupplierId, setOrderSupplierId] = useState(searchParams.get("order_supplier_id") || "");
  const [orderProductCategoryId, setOrderProductCategoryId] = useState(searchParams.get("order_product_category_id") || "");
  const [orderStatus, setOrderStatus] = useState(searchParams.get("order_status") || "");
  const [orderCustomerSearch, setOrderCustomerSearch] = useState(searchParams.get("order_customer_search") || "");
  const [orderPage, setOrderPage] = useState(parseInt(searchParams.get("order_page") || "1", 10));
  const [orderPageSize, setOrderPageSize] = useState(parseInt(searchParams.get("order_page_size") || "25", 10));

  // Reference lists
  const [productCategories, setProductCategories] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // Data states
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  // Order Data states
  const [orderItems, setOrderItems] = useState([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderSummary, setOrderSummary] = useState(null);
  const [orderLoading, setOrderLoading] = useState(true);

  const loadReferences = useCallback(async () => {
    try {
      const [pcs, sps, sups] = await Promise.all([
        api.get("/product-categories"),
        api.get("/users/salespeople"),
        api.get("/suppliers"),
      ]);
      setProductCategories(pcs.data);
      setSalespeople(sps.data);
      setSuppliers(sups.data);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    loadReferences();
  }, [loadReferences]);

  useEffect(() => {
    setActiveTab(searchParams.get("tab") || "customer");

    setStartDate(searchParams.get("start_date") || defStart);
    setEndDate(searchParams.get("end_date") || defEnd);
    setDateType(searchParams.get("date_type") || "Sözleşme Tarihi");
    setSalespersonId(searchParams.get("salesperson_id") || "");
    setProductCategoryId(searchParams.get("product_category_id") || "");
    setStatus(searchParams.getAll("status").filter(s => s && s !== "__all__"));
    setIncludeQuotations(searchParams.get("include_quotations") === "true");
    setIncludeArchived(searchParams.get("include_archived") === "true");
    setIncludeUndated(searchParams.get("include_undated") === "true");
    setPage(parseInt(searchParams.get("page") || "1", 10));
    setPageSize(parseInt(searchParams.get("page_size") || "25", 10));

    setOrderStartDate(searchParams.get("order_start_date") || defStart);
    setOrderEndDate(searchParams.get("order_end_date") || defEnd);
    setOrderSupplierId(searchParams.get("order_supplier_id") || "");
    setOrderProductCategoryId(searchParams.get("order_product_category_id") || "");
    setOrderStatus(searchParams.get("order_status") || "");
    setOrderCustomerSearch(searchParams.get("order_customer_search") || "");
    setOrderPage(parseInt(searchParams.get("order_page") || "1", 10));
    setOrderPageSize(parseInt(searchParams.get("order_page_size") || "25", 10));
  }, [searchParams, defStart, defEnd]);

  const isAllSelected = status.length === JOB_STATUS_FLOW.length;

  const handleToggleAll = () => {
    if (isAllSelected) {
      setStatus([]);
    } else {
      setStatus(JOB_STATUS_FLOW.map(([k]) => k));
    }
  };

  const handleToggleStatus = (key) => {
    setStatus(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      if (arr.includes(key)) {
        return arr.filter(k => k !== key);
      } else {
        return [...arr, key];
      }
    });
  };

  const statusSummary = useMemo(() => {
    if (!status || status.length === 0) {
      return "Tüm Durumlar";
    }
    if (status.length === JOB_STATUS_FLOW.length) {
      return "Tüm Durumlar";
    }
    if (status.length > 2) {
      return `${status.length} Durum Seçili`;
    }
    return status.map(k => JOB_STATUS_LABELS[k] || k).join(", ");
  }, [status]);

  const loadReport = useCallback(async (customPage = page, customPageSize = pageSize) => {
    setLoading(true);
    try {
      const searchParamsObj = new URLSearchParams();
      searchParamsObj.set("page", customPage);
      searchParamsObj.set("page_size", customPageSize);
      searchParamsObj.set("date_type", dateType);

      if (startDate) {
        searchParamsObj.set("start_date", `${startDate}T00:00:00+03:00`);
      }
      if (endDate) {
        searchParamsObj.set("end_date", `${endDate}T23:59:59+03:00`);
      }
      if (salespersonId) searchParamsObj.set("salesperson_id", salespersonId);
      if (productCategoryId) searchParamsObj.set("product_category_id", productCategoryId);

      if (status && status.length > 0) {
        status.forEach(st => {
          if (st && st !== "__all__") {
            searchParamsObj.append("status", st);
          }
        });
      }

      if (includeQuotations) searchParamsObj.set("include_quotations", "true");
      if (includeArchived) searchParamsObj.set("include_archived", "true");
      if (includeUndated) searchParamsObj.set("include_undated", "true");

      const r = await api.get(`/reports/jobs?${searchParamsObj.toString()}`);
      setItems(r.data.items);
      setTotal(r.data.total);
      setTotals(r.data.totals);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, dateType, salespersonId, productCategoryId, status, includeQuotations, includeArchived, includeUndated, page, pageSize]);

  const loadOrderReport = useCallback(async (customPage = orderPage, customPageSize = orderPageSize) => {
    setOrderLoading(true);
    try {
      const searchParamsObj = new URLSearchParams();
      searchParamsObj.set("page", customPage);
      searchParamsObj.set("page_size", customPageSize);

      if (orderStartDate) {
        searchParamsObj.set("start_date", `${orderStartDate}T00:00:00+03:00`);
      }
      if (orderEndDate) {
        searchParamsObj.set("end_date", `${orderEndDate}T23:59:59+03:00`);
      }
      if (orderSupplierId) searchParamsObj.set("supplier_id", orderSupplierId);
      if (orderProductCategoryId) searchParamsObj.set("product_category_id", orderProductCategoryId);
      if (orderStatus) searchParamsObj.set("status", orderStatus);
      if (orderCustomerSearch) searchParamsObj.set("customer_search", orderCustomerSearch);

      const r = await api.get(`/reports/orders?${searchParamsObj.toString()}`);
      setOrderItems(r.data.items);
      setOrderTotal(r.data.total);
      setOrderSummary(r.data.summary);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOrderLoading(false);
    }
  }, [orderStartDate, orderEndDate, orderSupplierId, orderProductCategoryId, orderStatus, orderCustomerSearch, orderPage, orderPageSize]);

  useEffect(() => {
    if (activeTab === "customer") {
      loadReport();
    } else {
      loadOrderReport();
    }
  }, [activeTab, loadReport, loadOrderReport]);

  const updateUrlParams = (newParams) => {
    const updated = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([k, v]) => {
      if (k === "status") {
        updated.delete("status");
        if (Array.isArray(v)) {
          v.forEach(val => {
            if (val && val !== "__all__") {
              updated.append("status", val);
            }
          });
        } else if (v && v !== "__all__") {
          updated.set("status", v);
        }
      } else {
        if (v === null || v === undefined || v === "") {
          updated.delete(k);
        } else {
          updated.set(k, v);
        }
      }
    });
    setSearchParams(updated);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    updateUrlParams({
      start_date: startDate,
      end_date: endDate,
      date_type: dateType,
      salesperson_id: salespersonId,
      product_category_id: productCategoryId,
      status: status,
      include_quotations: includeQuotations ? "true" : "",
      include_archived: includeArchived ? "true" : "",
      include_undated: includeUndated ? "true" : "",
      page: "1",
    });
  };

  const handleOrderFilterSubmit = (e) => {
    e.preventDefault();
    updateUrlParams({
      order_start_date: orderStartDate,
      order_end_date: orderEndDate,
      order_supplier_id: orderSupplierId,
      order_product_category_id: orderProductCategoryId,
      order_status: orderStatus,
      order_customer_search: orderCustomerSearch,
      order_page: "1",
    });
  };

  const handleClearFilters = () => {
    const defaults = {
      start_date: defStart,
      end_date: defEnd,
      date_type: "Sözleşme Tarihi",
      salesperson_id: "",
      product_category_id: "",
      status: [],
      include_quotations: "",
      include_archived: "",
      include_undated: "",
      page: "1",
    };
    setStartDate(defStart);
    setEndDate(defEnd);
    setDateType("Sözleşme Tarihi");
    setSalespersonId("");
    setProductCategoryId("");
    setStatus([]);
    setIncludeQuotations(false);
    setIncludeArchived(false);
    setIncludeUndated(false);
    setPage(1);
    updateUrlParams(defaults);
  };

  const handleClearOrderFilters = () => {
    const defaults = {
      order_start_date: defStart,
      order_end_date: defEnd,
      order_supplier_id: "",
      order_product_category_id: "",
      order_status: "",
      order_customer_search: "",
      order_page: "1",
    };
    setOrderStartDate(defStart);
    setOrderEndDate(defEnd);
    setOrderSupplierId("");
    setOrderProductCategoryId("");
    setOrderStatus("");
    setOrderCustomerSearch("");
    setOrderPage(1);
    updateUrlParams(defaults);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    updateUrlParams({ page: String(newPage) });
  };

  const handleOrderPageChange = (newPage) => {
    setOrderPage(newPage);
    updateUrlParams({ order_page: String(newPage) });
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    updateUrlParams({ page: "1", page_size: String(newSize) });
  };

  const handleOrderPageSizeChange = (newSize) => {
    setOrderPageSize(newSize);
    setOrderPage(1);
    updateUrlParams({ order_page: "1", order_page_size: String(newSize) });
  };

  const handleExport = async () => {
    try {
      const searchParamsObj = new URLSearchParams();
      searchParamsObj.set("date_type", dateType);

      if (startDate) {
        searchParamsObj.set("start_date", `${startDate}T00:00:00+03:00`);
      }
      if (endDate) {
        searchParamsObj.set("end_date", `${endDate}T23:59:59+03:00`);
      }
      if (salespersonId) searchParamsObj.set("salesperson_id", salespersonId);
      if (productCategoryId) searchParamsObj.set("product_category_id", productCategoryId);

      if (status && status.length > 0) {
        status.forEach(st => {
          if (st && st !== "__all__") {
            searchParamsObj.append("status", st);
          }
        });
      }

      if (includeQuotations) searchParamsObj.set("include_quotations", "true");
      if (includeArchived) searchParamsObj.set("include_archived", "true");
      if (includeUndated) searchParamsObj.set("include_undated", "true");

      const response = await api.get(`/reports/jobs/export?${searchParamsObj.toString()}`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      let filename = "Yonetim_Raporu.xlsx";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Yönetim raporu başarıyla indirildi.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handlePdfExport = async () => {
    try {
      const searchParamsObj = new URLSearchParams();
      searchParamsObj.set("date_type", dateType);

      if (startDate) {
        searchParamsObj.set("start_date", `${startDate}T00:00:00+03:00`);
      }
      if (endDate) {
        searchParamsObj.set("end_date", `${endDate}T23:59:59+03:00`);
      }
      if (salespersonId) searchParamsObj.set("salesperson_id", salespersonId);
      if (productCategoryId) searchParamsObj.set("product_category_id", productCategoryId);

      if (status && status.length > 0) {
        status.forEach(st => {
          if (st && st !== "__all__") {
            searchParamsObj.append("status", st);
          }
        });
      }

      if (includeQuotations) searchParamsObj.set("include_quotations", "true");
      if (includeArchived) searchParamsObj.set("include_archived", "true");
      if (includeUndated) searchParamsObj.set("include_undated", "true");

      const response = await api.get(`/reports/jobs/export/pdf?${searchParamsObj.toString()}`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      let filename = "Yonetim_Raporu.pdf";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Yönetim PDF raporu başarıyla oluşturuldu.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    updateUrlParams({ tab: tabName });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight flex items-center gap-2">
            <BarChart3 size={24} className="text-[#168DEB]" /> Raporlar
          </h1>
          <p className="text-xs text-slate-500 mt-1">Sözleşme, kapanış süreçlerini ve sipariş dökümlerini tarihsel bazda izleyin.</p>
        </div>
        {activeTab === "customer" && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={handleExport}
              className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium"
              data-testid="reports-export-excel"
            >
              📊 Yönetim Raporunu İndir
            </Button>
            <Button
              type="button"
              onClick={handlePdfExport}
              className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium"
              data-testid="reports-export-pdf"
            >
              📄 PDF Raporu Oluştur
            </Button>
          </div>
        )}
      </div>

      {/* Two-Tab Structure */}
      <div className="flex border-b border-[#DCE2E8]">
        <button
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            activeTab === "customer"
              ? "border-[#FF353A] text-[#FF353A]"
              : "border-transparent text-slate-500 hover:text-[#171A1F]"
          }`}
          onClick={() => handleTabChange("customer")}
          data-testid="tab-customer-report"
        >
          İş ve Kârlılık Raporu
        </button>
        <button
          className={`px-4 py-2 text-sm font-bold border-b-2 transition-colors ${
            activeTab === "order"
              ? "border-[#FF353A] text-[#FF353A]"
              : "border-transparent text-slate-500 hover:text-[#171A1F]"
          }`}
          onClick={() => handleTabChange("order")}
          data-testid="tab-order-report"
        >
          Sipariş Raporu
        </button>
      </div>

      {activeTab === "customer" ? (
        <>
          {/* İş ve Kârlılık Raporu — FILTERS */}
          <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 shadow-sm">
            <form onSubmit={handleFilterSubmit} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-6 items-end">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Başlangıç Tarihi</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border-[#DCE2E8]"
                    data-testid="reports-start-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Bitiş Tarihi</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border-[#DCE2E8]"
                    data-testid="reports-end-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Tarih Türü</label>
                  <Select value={dateType} onValueChange={setDateType}>
                    <SelectTrigger className="border-[#DCE2E8]" data-testid="reports-date-type-filter"><SelectValue placeholder="Tarih Türü" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#DCE2E8]">
                      <SelectItem value="Sözleşme Tarihi">Sözleşme Tarihi</SelectItem>
                      <SelectItem value="Kapanış Tarihi">Kapanış Tarihi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Satış Personeli</label>
                  <Select value={salespersonId || "__all__"} onValueChange={(v) => setSalespersonId(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="border-[#DCE2E8]" data-testid="reports-salesperson-filter"><SelectValue placeholder="Tüm Personel" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#DCE2E8]">
                      <SelectItem value="__all__">Tüm Personel</SelectItem>
                      {salespeople.map((sp) => (
                        <SelectItem key={sp.id} value={sp.id}>{sp.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Ürün Grubu</label>
                  <Select value={productCategoryId || "__all__"} onValueChange={(v) => setProductCategoryId(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="border-[#DCE2E8]" data-testid="reports-product-filter"><SelectValue placeholder="Tüm Ürünler" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#DCE2E8]">
                      <SelectItem value="__all__">Tüm Ürünler</SelectItem>
                      {productCategories.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">İş Durumu</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-9 justify-between text-left font-normal border-[#DCE2E8] bg-white hover:bg-[#F4F6F8] text-[#171A1F] px-3 text-sm flex items-center shadow-sm"
                        data-testid="reports-status-filter"
                      >
                        <span className="truncate">{statusSummary}</span>
                        <span className="text-slate-500 text-[10px]">▼</span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-60 p-0 bg-white border border-[#DCE2E8] shadow-md rounded-md" align="start">
                      <div className="p-2 border-b border-[#DCE2E8] flex items-center justify-between bg-[#F4F6F8]">
                        <span className="text-xs font-semibold text-[#171A1F]">Durum Seçimi</span>
                        {status.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setStatus([])}
                            className="text-xs text-rose-600 hover:text-rose-700 font-medium"
                          >
                            Temizle
                          </button>
                        )}
                      </div>
                      <div className="p-2 border-b border-[#DCE2E8]">
                        <label className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F4F6F8] cursor-pointer select-none">
                          <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={handleToggleAll}
                            data-testid="status-select-all"
                          />
                          <span className="text-sm font-medium text-[#171A1F]">Tümünü Seç</span>
                        </label>
                      </div>
                      <div className="max-h-60 overflow-y-auto p-1 space-y-0.5">
                        {JOB_STATUS_FLOW.map(([k, l]) => {
                          const checked = status.includes(k);
                          return (
                            <label
                              key={k}
                              className="flex items-center gap-2 px-3 py-1 rounded hover:bg-[#F4F6F8] cursor-pointer select-none"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => handleToggleStatus(k)}
                                data-testid={`status-option-${k}`}
                              />
                              <span className="text-sm text-slate-700">{l}</span>
                            </label>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Kapsam / Şeffaf Hariç Tutma Seçenekleri */}
              <div className="flex flex-wrap items-center gap-6 pt-2 pb-1 text-xs text-slate-700 border-t border-slate-100" data-testid="reports-scope-options">
                <span className="font-semibold text-slate-500 uppercase tracking-wide">Rapor Kapsamı:</span>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Checkbox
                    checked={includeQuotations}
                    onCheckedChange={(checked) => setIncludeQuotations(!!checked)}
                    data-testid="filter-include-quotations"
                  />
                  <span>Teklifleri Dahil Et</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Checkbox
                    checked={includeArchived}
                    onCheckedChange={(checked) => setIncludeArchived(!!checked)}
                    data-testid="filter-include-archived"
                  />
                  <span>Arşivlenmişleri Dahil Et</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Checkbox
                    checked={includeUndated}
                    onCheckedChange={(checked) => setIncludeUndated(!!checked)}
                    data-testid="filter-include-undated"
                  />
                  <span>Tarihsizleri Dahil Et</span>
                </label>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium" data-testid="reports-filter-apply">
                  Filtrele
                </Button>
                <Button type="button" variant="outline" onClick={handleClearFilters} className="border-[#DCE2E8] text-slate-700" data-testid="reports-filter-clear">
                  Temizle
                </Button>
              </div>
            </form>
          </div>

          {/* Customer Totals */}
          {totals && (
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-5" data-testid="reports-totals">
              <KpiCard icon={Briefcase} label="Toplam İş Sayısı" value={totals.total_jobs} testId="report-total-jobs" />
              <KpiCard icon={Coins} label="Toplam Satış" value={formatCurrency(totals.total_sales)} testId="report-total-sales" />
              <KpiCard icon={TrendingDown} label="Toplam Maliyet" value={formatCurrency(totals.total_cost)} testId="report-total-cost" />
              <KpiCard icon={TrendingUp} label="Toplam Brüt Kâr" value={formatCurrency(totals.total_gross_profit)} testId="report-total-profit" />
              <KpiCard icon={Percent} label="Ağırlıklı Brüt Marj" value={`%${totals.weighted_gross_margin}`} testId="report-weighted-margin" />
            </div>
          )}

          {/* Customer Results Table */}
          <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-slate-500" data-testid="reports-loading">
                <Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                testId="reports-empty"
                title="Kayıt bulunamadı"
                description="Lütfen filtreleme kriterlerinizi değiştirip tekrar deneyin."
              />
            ) : (
              <div className="overflow-x-auto scrollbar-thin">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İş No</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Müşteri</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sözleşme Tarihi</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Kapanış Tarihi</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Satış Personeli</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ürün Grubu / Ürün Grupları</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Durum</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Satış Tutarı</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Toplam Maliyet</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Brüt Kâr</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Brüt Kâr Marjı</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                  {items.map((it) => (
                    <TableRow
                      key={it.id}
                      className="gp-row cursor-pointer border-b border-[#DCE2E8]"
                      onClick={() => navigate(`/isler/${it.id}`)}
                      data-testid={`report-row-${it.job_number}`}
                    >
                      <TableCell className="font-mono text-sm font-semibold text-[#FF353A]">{it.job_number}</TableCell>
                      <TableCell className="text-sm font-medium text-[#171A1F]">{it.customer_name}</TableCell>
                      <TableCell className="text-sm text-slate-700">{formatDate(it.contract_date)}</TableCell>
                      <TableCell className="text-sm text-slate-700">{formatDate(it.closing_date)}</TableCell>
                      <TableCell className="text-sm text-slate-700">{it.salesperson_name || "-"}</TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {it.product_group_names?.length ? it.product_group_names.join(", ") : "-"}
                      </TableCell>
                      <TableCell>
                        <StatusBadge tone={jobStatusTone(it.status)}>
                          {JOB_STATUS_LABELS[it.status] || it.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(it.sales_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(it.total_cost)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        <ProfitCell value={it.gross_profit} showLabel={false} />
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        <MarginCell value={it.gross_profit_margin} minMargin={minMargin} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
            {!loading && items.length > 0 && (
              <Pagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </>
      ) : (
        <>
          {/* Sipariş Raporu — FILTERS */}
          <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 shadow-sm">
            <form onSubmit={handleOrderFilterSubmit} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-6 items-end">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Başlangıç Tarihi</label>
                  <Input
                    type="date"
                    value={orderStartDate}
                    onChange={(e) => setOrderStartDate(e.target.value)}
                    className="border-[#DCE2E8]"
                    data-testid="orders-start-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Bitiş Tarihi</label>
                  <Input
                    type="date"
                    value={orderEndDate}
                    onChange={(e) => setOrderEndDate(e.target.value)}
                    className="border-[#DCE2E8]"
                    data-testid="orders-end-date"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Tedarikçi</label>
                  <Select value={orderSupplierId || "__all__"} onValueChange={(v) => setOrderSupplierId(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="border-[#DCE2E8]" data-testid="orders-supplier-filter"><SelectValue placeholder="Tüm Tedarikçiler" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#DCE2E8]">
                      <SelectItem value="__all__">Tüm Tedarikçiler</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Ürün Grubu</label>
                  <Select value={orderProductCategoryId || "__all__"} onValueChange={(v) => setOrderProductCategoryId(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="border-[#DCE2E8]" data-testid="orders-product-filter"><SelectValue placeholder="Tüm Ürünler" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#DCE2E8]">
                      <SelectItem value="__all__">Tüm Ürünler</SelectItem>
                      {productCategories.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Sipariş Durumu</label>
                  <Select value={orderStatus || "__all__"} onValueChange={(v) => setOrderStatus(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="border-[#DCE2E8]" data-testid="orders-status-filter"><SelectValue placeholder="Tüm Durumlar" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#DCE2E8]">
                      <SelectItem value="__all__">Tüm Durumlar</SelectItem>
                      {ORDER_STATUS_FLOW.map(([k, l]) => (
                        <SelectItem key={k} value={k}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Müşteri Ara</label>
                  <div className="relative">
                    <Input
                      placeholder="Müşteri adı yazın..."
                      value={orderCustomerSearch}
                      onChange={(e) => setOrderCustomerSearch(e.target.value)}
                      data-testid="orders-customer-search"
                      className="pr-8 border-[#DCE2E8]"
                    />
                    <Search className="absolute right-2.5 top-2.5 text-slate-400" size={16} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm font-medium" data-testid="orders-filter-apply">
                  Filtrele
                </Button>
                <Button type="button" variant="outline" onClick={handleClearOrderFilters} className="border-[#DCE2E8] text-slate-700" data-testid="orders-filter-clear">
                  Temizle
                </Button>
              </div>
            </form>
          </div>

          {/* Sipariş Raporu — COMPACT SUMMARY BANNER */}
          {orderSummary && (
            <div className="bg-white rounded-lg p-3 border border-[#DCE2E8] shadow-sm text-xs text-slate-700 flex flex-wrap gap-x-4 gap-y-2 items-center" data-testid="orders-summary">
              <span className="font-semibold text-[#171A1F] pr-2 border-r border-[#DCE2E8]">
                Toplam Sipariş: <span className="text-sm font-bold font-mono text-[#FF353A]">{orderSummary.total}</span>
              </span>
              <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                {ORDER_STATUS_FLOW.map(([k, l]) => {
                  const count = orderSummary[k] || 0;
                  return (
                    <span key={k} className="inline-flex items-center gap-1">
                      <span className="text-slate-500">{l}:</span>
                      <span className="font-bold font-mono text-[#171A1F] bg-[#F4F6F8] border border-[#DCE2E8] px-1.5 py-0.5 rounded text-[11px]">{count}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sipariş Results Table */}
          <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
            {orderLoading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-slate-500" data-testid="orders-loading">
                <Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...
              </div>
            ) : orderItems.length === 0 ? (
              <EmptyState
                testId="orders-empty"
                title="Kayıt bulunamadı"
                description="Lütfen filtreleme kriterlerinizi değiştirip tekrar deneyin."
              />
            ) : (
              <div className="overflow-x-auto scrollbar-thin">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sipariş No</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Tedarikçi</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Müşteri</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sipariş Tarihi</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ürün Grubu</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Açıklama</TableHead>
                      <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sipariş Durumu</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                  {orderItems.map((it) => (
                    <TableRow
                      key={it.id}
                      className="gp-row cursor-pointer border-b border-[#DCE2E8]"
                      onClick={() => navigate(`/siparisler/${it.id}`)}
                      data-testid={`order-row-${it.order_number}`}
                    >
                      <TableCell className="font-mono text-sm font-semibold text-[#FF353A]">{it.order_number}</TableCell>
                      <TableCell className="text-sm font-medium text-[#171A1F]">{it.supplier_name || "-"}</TableCell>
                      <TableCell className="text-sm text-slate-700">{it.customer_name}</TableCell>
                      <TableCell className="text-sm text-slate-700">{formatDate(it.order_date)}</TableCell>
                      <TableCell className="text-sm text-slate-700">{it.product_category_name || "-"}</TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-xs truncate">{it.description || "-"}</TableCell>
                      <TableCell>
                        <StatusBadge tone={orderStatusTone(it.status)}>
                          {ORDER_STATUS_LABELS[it.status] || it.status}
                        </StatusBadge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            )}
            {!orderLoading && orderItems.length > 0 && (
              <Pagination
                page={orderPage}
                pageSize={orderPageSize}
                total={orderTotal}
                onPageChange={handleOrderPageChange}
                onPageSizeChange={handleOrderPageSizeChange}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, testId }) {
  return (
    <Card className="bg-white border-[#DCE2E8] shadow-sm" data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</CardTitle>
        <Icon size={14} className="text-[#168DEB]" />
      </CardHeader>
      <CardContent>
        <div className="font-mono text-xl font-bold text-[#171A1F]">{value ?? "-"}</div>
      </CardContent>
    </Card>
  );
}
